import React, { useEffect, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import BottomSheet from "./bottom-sheet";
import { cn } from "../../lib/utils";

interface MobileSearchableSelectOption {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

interface MobileSearchableSelectProps {
  options: MobileSearchableSelectOption[];
  onSelect: (value: string) => void;
  value?: string;
  defaultLabel?: string;
  className?: string;
}

const MobileSearchableSelect: React.FC<MobileSearchableSelectProps> = ({
  options,
  onSelect,
  value,
  defaultLabel,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedLabel = value ? options.find((option) => option.value === value)?.label : undefined;
  const filteredOptions = (options ?? []).filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const choose = (nextValue: string) => {
    onSelect(nextValue);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex h-11 w-full items-center justify-between gap-2 rounded-xl border-0 bg-white px-3.5 text-left text-[15px] shadow-[0_1px_2px_rgba(13,14,19,0.04)] ring-1 ring-gray-100 active:bg-gray-50",
          className,
        )}
      >
        <span className={cn("truncate", selectedLabel ? "text-gray-900" : "text-gray-400")}>
          {selectedLabel ?? defaultLabel ?? "Select an option"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={defaultLabel ?? "Select"}
        size="full"
        headerAction={
          value ? (
            <button
              type="button"
              onClick={() => choose("")}
              className="border-0 bg-transparent px-2 text-sm font-semibold text-primary-600 active:opacity-60"
            >
              Clear
            </button>
          ) : undefined
        }
      >
        <div className="sticky top-0 z-10 bg-white px-4 pb-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search…"
              className="h-11 w-full rounded-xl border-0 bg-gray-50 pl-10 pr-3 text-base text-gray-900 ring-1 ring-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <ul className="px-4 pb-4">
          {filteredOptions.length === 0 ? (
            <li className="py-10 text-center text-sm text-gray-500">No results found</li>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    disabled={option.disabled}
                    onClick={() => choose(option.value)}
                    className={cn(
                      "flex min-h-[48px] w-full items-center gap-3 border-0 border-b border-gray-50 bg-transparent px-1 py-2.5 text-left text-[15px] active:bg-gray-50 disabled:text-gray-300",
                      isSelected ? "font-semibold text-primary-700" : "text-gray-900",
                    )}
                  >
                    {option.icon && <span className={`bi bi-${option.icon}`} />}
                    <span className="flex-1 break-words">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-primary-600" />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </BottomSheet>
    </>
  );
};

export default MobileSearchableSelect;
